import React, { useState } from 'react'

const UPGRADE_ROLES = [
  { id: 'GP', label: 'GP Upgrade' },
  { id: 'LP', label: 'LP Upgrade' },
  { id: 'Startup', label: 'Startup Upgrade' },
  { id: 'membership', label: 'Membership Tiers' },
  { id: 'serviceProvider', label: 'Service Provider' },
]

const MEMBERSHIP_IDS = ['tier1', 'tier2', 'tier3']
const SP_IDS = ['sp_tier1', 'sp_tier2', 'sp_tier3']

const MEMBERSHIP_LABELS = {
  tier1: 'Standard (Basic)',
  tier2: 'Professional (Premium)',
  tier3: 'Enterprise',
  sp_tier1: 'SP 10GB',
  sp_tier2: 'SP 50GB',
  sp_tier3: 'SP 200GB',
}

function NumInput({ value, onChange, min = 0, step = 1, style }) {
  return (
    <input
      className="admin-field-input admin-pricing-num"
      type="number"
      min={min}
      step={step}
      value={value ?? 0}
      onChange={(e) => onChange(Number(e.target.value))}
      style={style}
    />
  )
}

function TextInput({ value, onChange, placeholder, style }) {
  return (
    <input
      className="admin-field-input"
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={style}
    />
  )
}

function PricingTable({ columns, rows, onChangeRow }) {
  if (!rows?.length) {
    return <p className="admin-pricing-empty">No items configured.</p>
  }
  return (
    <div className="admin-table-card embedded admin-pricing-table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id || index}>
              {columns.map((col) => (
                <td key={col.key}>
                  {col.type === 'number' ? (
                    <NumInput
                      value={row[col.key]}
                      onChange={(v) => onChangeRow(index, { [col.key]: v })}
                    />
                  ) : (
                    <TextInput
                      value={row[col.key]}
                      onChange={(v) => onChangeRow(index, { [col.key]: v })}
                      placeholder={col.placeholder}
                    />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function UpgradeRoleEditor({ role, catalog, onPatch }) {
  if (!catalog) return null
  const currency = catalog.currency === 'USD' ? '$' : '¥'

  const patchCatalog = (data) => onPatch(role, data)
  const patchArray = (field, index, itemPatch) => {
    const next = [...(catalog[field] || [])]
    next[index] = { ...next[index], ...itemPatch }
    patchCatalog({ [field]: next })
  }

  return (
    <div className="admin-pricing-role-body">
      <div className="admin-settings-grid-2">
        <div className="admin-field-group">
          <label className="admin-field-label">Currency</label>
          <select
            className="admin-field-input"
            value={catalog.currency || 'CNY'}
            onChange={(e) => patchCatalog({ currency: e.target.value })}
          >
            <option value="CNY">CNY (¥)</option>
            <option value="USD">USD ($)</option>
          </select>
        </div>
        {catalog.successFeePercent != null && (
          <div className="admin-field-group">
            <label className="admin-field-label">Success Fee (%)</label>
            <NumInput
              value={catalog.successFeePercent}
              onChange={(v) => patchCatalog({ successFeePercent: v })}
              step={0.1}
            />
          </div>
        )}
        {role === 'GP' && (
          <div className="admin-field-group">
            <label className="admin-field-label">Email Campaign Price ({currency})</label>
            <NumInput
              value={catalog.emailCampaignPrice}
              onChange={(v) => patchCatalog({ emailCampaignPrice: v })}
            />
          </div>
        )}
        {role === 'Startup' && (
          <div className="admin-field-group">
            <label className="admin-field-label">Domestic Display Fee ({currency}/year)</label>
            <NumInput
              value={catalog.domesticDisplayPrice}
              onChange={(v) => patchCatalog({ domesticDisplayPrice: v })}
            />
          </div>
        )}
      </div>

      {catalog.tiers?.length > 0 && (
        <div className="admin-pricing-block">
          <h4 className="admin-pricing-block-title">Base Fee Tiers (monthly {currency})</h4>
          <PricingTable
            columns={[
              { key: 'range', label: 'Range / Label', type: 'text', placeholder: 'AUM range' },
              { key: 'monthly', label: `Monthly (${currency})`, type: 'number' },
            ]}
            rows={catalog.tiers}
            onChangeRow={(i, patch) => patchArray('tiers', i, patch)}
          />
        </div>
      )}

      {catalog.recurring?.length > 0 && (
        <div className="admin-pricing-block">
          <h4 className="admin-pricing-block-title">Recurring Membership (monthly $)</h4>
          <PricingTable
            columns={[
              { key: 'name', label: 'Name', type: 'text' },
              { key: 'desc', label: 'Description', type: 'text' },
              { key: 'price', label: 'Monthly ($)', type: 'number' },
            ]}
            rows={catalog.recurring}
            onChangeRow={(i, patch) => patchArray('recurring', i, patch)}
          />
        </div>
      )}

      {catalog.roadshow?.length > 0 && (
        <div className="admin-pricing-block">
          <h4 className="admin-pricing-block-title">Roadshow Service</h4>
          <PricingTable
            columns={[
              { key: 'name', label: 'Name', type: 'text' },
              { key: 'desc', label: 'Description', type: 'text' },
              { key: 'price', label: `Price (${currency})`, type: 'number' },
              { key: 'sub', label: 'Unit Label', type: 'text', placeholder: '/5 LP' },
            ]}
            rows={catalog.roadshow}
            onChangeRow={(i, patch) => patchArray('roadshow', i, patch)}
          />
        </div>
      )}

      {catalog.summit?.length > 0 && (
        <div className="admin-pricing-block">
          <h4 className="admin-pricing-block-title">Summit Sponsorship</h4>
          <PricingTable
            columns={[
              { key: 'name', label: 'Name', type: 'text' },
              { key: 'desc', label: 'Description', type: 'text' },
              { key: 'price', label: `Price (${currency})`, type: 'number' },
            ]}
            rows={catalog.summit}
            onChangeRow={(i, patch) => patchArray('summit', i, patch)}
          />
        </div>
      )}

      {catalog.storage?.length > 0 && (
        <div className="admin-pricing-block">
          <h4 className="admin-pricing-block-title">Cloud Storage (monthly {currency})</h4>
          <PricingTable
            columns={[
              { key: 'name', label: 'Name', type: 'text' },
              { key: 'desc', label: 'Description', type: 'text' },
              { key: 'price', label: `Monthly (${currency})`, type: 'number' },
              { key: 'sub', label: 'Capacity Label', type: 'text', placeholder: '200GB Storage' },
            ]}
            rows={catalog.storage}
            onChangeRow={(i, patch) => patchArray('storage', i, patch)}
          />
        </div>
      )}
    </div>
  )
}

function MembershipPlanEditor({ planIds, plans, onPatchPlan }) {
  return (
    <div className="admin-settings-grid-2">
      {planIds.map((id) => {
        const plan = plans[id] || {}
        return (
          <div key={id} className="admin-card admin-plan-card">
            <div className="admin-card-header">
              <span className="admin-card-title">{MEMBERSHIP_LABELS[id] || id}</span>
              <label className="admin-toggle">
                <input
                  type="checkbox"
                  checked={plan.active !== false}
                  onChange={(e) => onPatchPlan(id, { active: e.target.checked })}
                />
                <span className="admin-toggle-slider" />
              </label>
            </div>
            <div className="admin-card-body">
              <div className="admin-field-group">
                <label className="admin-field-label">Display Name</label>
                <TextInput value={plan.name} onChange={(v) => onPatchPlan(id, { name: v })} />
              </div>
              <div className="admin-field-group">
                <label className="admin-field-label">Monthly Price (CNY)</label>
                <NumInput value={plan.price} onChange={(v) => onPatchPlan(id, { price: v })} />
              </div>
              {plan.contactQuota != null && (
                <div className="admin-field-group">
                  <label className="admin-field-label">Contacts / month</label>
                  <NumInput value={plan.contactQuota} onChange={(v) => onPatchPlan(id, { contactQuota: v })} />
                </div>
              )}
              {plan.companySeats != null && (
                <div className="admin-field-group">
                  <label className="admin-field-label">Company seats</label>
                  <NumInput value={plan.companySeats} onChange={(v) => onPatchPlan(id, { companySeats: v })} />
                </div>
              )}
              {plan.storageGb != null && (
                <div className="admin-field-group">
                  <label className="admin-field-label">Storage (GB)</label>
                  <NumInput value={plan.storageGb} onChange={(v) => onPatchPlan(id, { storageGb: v })} />
                </div>
              )}
              <div className="admin-plan-price-preview">
                ¥{plan.price || 0}<span>/month</span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function AdminPricingEditor({ upgradePricing = {}, membershipPlans = {}, onPatchUpgrade, onPatchMembership }) {
  const [roleTab, setRoleTab] = useState('GP')

  const patchUpgradeRole = (role, data) => {
    onPatchUpgrade({
      [role]: { ...(upgradePricing[role] || {}), ...data },
    })
  }

  const patchMembershipPlan = (id, data) => {
    onPatchMembership({
      [id]: { ...(membershipPlans[id] || {}), ...data },
    })
  }

  return (
    <div className="admin-pricing-editor">
      <div className="admin-pricing-subtabs">
        {UPGRADE_ROLES.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`admin-pricing-subtab${roleTab === t.id ? ' active' : ''}`}
            onClick={() => setRoleTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {roleTab === 'membership' && (
        <MembershipPlanEditor planIds={MEMBERSHIP_IDS} plans={membershipPlans} onPatchPlan={patchMembershipPlan} />
      )}
      {roleTab === 'serviceProvider' && (
        <MembershipPlanEditor planIds={SP_IDS} plans={membershipPlans} onPatchPlan={patchMembershipPlan} />
      )}
      {(roleTab === 'GP' || roleTab === 'LP' || roleTab === 'Startup') && (
        <UpgradeRoleEditor
          role={roleTab}
          catalog={upgradePricing[roleTab]}
          onPatch={patchUpgradeRole}
        />
      )}
    </div>
  )
}
